import React, { useEffect, useMemo, useState } from "react";
import { MDBContainer, MDBBtn, MDBIcon } from "mdbreact";
import { get } from "api/index";
import { getCatalogMemberStatus } from "api/stripe/checkoutSession";
import RegisterCatalogModal from "../../components/PricingCatalogs/RegisterCatalogModal";

// Public daily market feed: the approved crawl history (BLS PPI series for
// war/geopolitics-sensitive construction inputs) published from the CurLight
// approval console. Same viewing contract as /pricing-catalogues: anyone can
// browse sources, dates and references; the numbers are locked until the
// viewer is a member ($19 registration or a logged-in account).
const getMarketFeed = () => get("/data/v2/market-feed?$limit=365", true);

const CATEGORY_LABELS = {
  fuel_energy: "Fuel & Energy",
  steel: "Steel",
  lumber: "Lumber",
  cement_concrete: "Cement & Concrete",
  construction_materials: "Construction Materials",
};

function DeltaCell({ value, locked }) {
  if (locked) return <span title="Members only">🔒</span>;
  if (value === null || value === undefined)
    return <span className="text-muted">–</span>;
  const up = value > 0;
  const color = up ? "#d9534f" : value < 0 ? "#28a745" : "#6c757d";
  return (
    <span style={{ color, fontWeight: 600 }}>
      {up ? "▲" : value < 0 ? "▼" : ""} {value.toFixed(2)}%
    </span>
  );
}

export default function MarketFeed() {
  const [rows, setRows] = useState(null);
  const [masked, setMasked] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getMarketFeed().catch(() => null),
      getCatalogMemberStatus().catch(() => null),
    ]).then(([feed, status]) => {
      if (cancelled) return;
      if (!feed || !Array.isArray(feed.rows)) {
        setError("The market feed is unavailable right now. Please try again later.");
        setRows([]);
        return;
      }
      setRows(feed.rows);
      setMasked(Boolean(feed.masked));
      setIsMember(Boolean(status?.canExport));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const days = useMemo(() => {
    const byDay = {};
    (rows || []).forEach((r) => {
      const day = String(r.publishedAt || "").slice(0, 10) || "Earlier";
      (byDay[day] = byDay[day] || []).push(r);
    });
    return Object.keys(byDay)
      .sort()
      .reverse()
      .map((day) => ({ day, items: byDay[day] }));
  }, [rows]);

  const locked = masked && !isMember;

  return (
    <MDBContainer fluid className="py-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-start flex-wrap">
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 className="mb-1">Daily Market Price Feed</h2>
          <p className="text-muted mb-2">
            How war and geopolitics move construction input prices — diesel,
            steel, lumber, cement and materials — tracked daily from official
            sources, reviewed before publication. Every figure links to its
            source so you can investigate it yourself.
          </p>
        </div>
        {locked ? (
          <MDBBtn color="primary" onClick={() => setRegisterOpen(true)}>
            Unlock prices — $19
          </MDBBtn>
        ) : null}
      </div>

      {locked ? (
        <div
          className="p-3 mb-3"
          style={{ background: "#f6f8fb", border: "1px solid #e3e9f2", borderRadius: 8 }}
        >
          <MDBIcon icon="lock" className="mr-2" />
          Sources, dates and references are free to browse. Index values and
          daily changes are visible to members —{" "}
          <a href="#unlock" onClick={(e) => { e.preventDefault(); setRegisterOpen(true); }}>
            register for a one-time $19
          </a>{" "}
          or <a href="/authorization">log in</a>.
        </div>
      ) : null}

      {error ? <div className="alert alert-warning">{error}</div> : null}

      {rows === null ? (
        <p className="text-muted">Loading feed…</p>
      ) : days.length === 0 && !error ? (
        <p className="text-muted">
          No published prices yet — the daily crawl stages new observations at
          03:00 UTC and they appear here once approved.
        </p>
      ) : (
        days.map(({ day, items }) => (
          <div key={day} className="mb-4">
            <h5 className="mb-2" style={{ fontWeight: 600 }}>
              {day}
            </h5>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ background: "#fff" }}>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Category</th>
                    <th>Period</th>
                    <th className="text-right">Index</th>
                    <th className="text-right">Change</th>
                    <th>References</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const ref = (r.sourceRefs && r.sourceRefs[0]) || {};
                    const extraRefs =
                      (r.eventTags || [])
                        .flatMap((t) => t.referenceUrls || [])
                        .filter((u) => u && u !== ref.evidenceUrl);
                    return (
                      <tr key={r._id}>
                        <td>{ref.name || r.sourceId}</td>
                        <td>{CATEGORY_LABELS[r.category] || r.category}</td>
                        <td>{r.period}</td>
                        <td className="text-right">
                          {locked || r.indexValue === null ? "🔒" : r.indexValue}
                        </td>
                        <td className="text-right">
                          <DeltaCell value={r.pctDelta} locked={locked || r.pctDelta === null} />
                        </td>
                        <td>
                          {ref.evidenceUrl ? (
                            <a href={ref.evidenceUrl} target="_blank" rel="noopener noreferrer">
                              {ref.seriesId || "source"}
                            </a>
                          ) : null}
                          {extraRefs.map((u, i) => (
                            <span key={u}>
                              {" · "}
                              <a href={u} target="_blank" rel="noopener noreferrer">
                                ref{i + 1}
                              </a>
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <RegisterCatalogModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
    </MDBContainer>
  );
}
